/**
 * PWAInstallPrompt Component
 *
 * Component that displays a custom install prompt for the PWA.
 * Shows a banner when the app is installable and handles the install flow.
 */

import { useEffect, useState, useCallback } from 'react'
import { Download, X } from 'lucide-react'
import { promptInstall, type BeforeInstallPromptEvent } from '@/lib/pwa'
import { useT } from '@/contexts/I18nContext'

interface PWAInstallPromptProps {
  onInstall?: () => void
  onDismiss?: () => void
}

export function PWAInstallPrompt({ onInstall, onDismiss }: PWAInstallPromptProps) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const t = useT()

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the default browser prompt
      e.preventDefault()
      // Store the event for later use
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setShowPrompt(true)
    }

    const handleAppInstalled = () => {
      // PWA was installed, hide the prompt
      setDeferredPrompt(null)
      setShowPrompt(false)
      onInstall?.()
    }

    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [onInstall])

  const handleInstallClick = useCallback(async () => {
    if (!deferredPrompt) {
      return
    }

    const accepted = await promptInstall(deferredPrompt)

    if (accepted) {
      // User accepted the install prompt
      setShowPrompt(false)
      setDeferredPrompt(null)
      onInstall?.()
    }
  }, [deferredPrompt, onInstall])

  const handleDismiss = useCallback(() => {
    setShowPrompt(false)
    setDeferredPrompt(null)
    onDismiss?.()
  }, [onDismiss])

  // Don't render if not installable or prompt is dismissed
  if (!showPrompt || !deferredPrompt) {
    return null
  }

  return (
    <div
      role="complementary"
      aria-label={t('pwa.installAppPrompt')}
      className="bg-primary-container text-on-primary-container animate-slide-down"
    >
      <div className="flex items-center gap-3 px-4 py-2.5">
        <Download className="w-5 h-5" strokeWidth={1.5} />
        <span className="flex-1 text-sm font-medium">
          {t('pwa.installPrompt')}
        </span>
        <button
          onClick={handleInstallClick}
          className="bg-primary text-on-primary px-3 py-1 rounded-full text-xs font-semibold transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
          aria-label={t('pwa.installApp')}
        >
          {t('pwa.install')}
        </button>
        <button
          onClick={handleDismiss}
          className="p-1 rounded-full hover:bg-on-primary-container/10 transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label={t('pwa.dismiss')}
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  )
}
